import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import ScrollableDropdown from '../components/ScrollableDropdown';

import axios from 'axios';
import toast from 'react-hot-toast';
import { ChartBarIcon, BugAntIcon, ExclamationCircleIcon, PlusIcon, ArrowLeftIcon, ClockIcon, InformationCircleIcon, RectangleStackIcon } from '@heroicons/react/24/outline';

const severityColors = {
  'Low': 'bg-green-100 text-green-800',
  'Medium': 'bg-yellow-100 text-yellow-800',
  'High': 'bg-red-100 text-red-800',
  'Critical': 'bg-red-100 text-red-800'
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedApp, setSelectedApp] = useState(() => localStorage.getItem('selectedApp') || '');

  const [stats, setStats] = useState(() => {
    const cached = sessionStorage.getItem('dashboardStats');
    return cached ? JSON.parse(cached) : { byStatus: [], bySeverity: [] };
  });
  const [applications, setApplications] = useState(() => {
    const cached = sessionStorage.getItem('dashboardApps');
    return cached ? JSON.parse(cached) : [];
  });

  useEffect(() => {
    if (user?.role === 'manager') navigate('/manager/dashboard');
  }, [user, navigate]);

  const fetchApplications = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/applications');
      const activeApps = data.data.filter(app => ['Active', 'Testing'].includes(app.status));
      setApplications(activeApps);
      sessionStorage.setItem('dashboardApps', JSON.stringify(activeApps));
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    }
  }, []);

  const handleAppChange = (appId) => {
    setSelectedApp(appId);
    if (appId) localStorage.setItem('selectedApp', appId);
    else localStorage.removeItem('selectedApp');
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      const statsUrl = selectedApp ? `/api/bugs/stats?application=${selectedApp}` : '/api/bugs/stats';
      const { data: statsRes } = await axios.get(statsUrl);
      setStats(statsRes.data);
      sessionStorage.setItem('dashboardStats', JSON.stringify(statsRes.data));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    }
  }, [selectedApp]);

  useEffect(() => {
    fetchDashboardData();
    fetchApplications();
  }, [fetchDashboardData, fetchApplications]);

  useEffect(() => {
    const handleBugUpdated = () => {
      sessionStorage.removeItem('dashboardStats');
      sessionStorage.removeItem('dashboardAppStats');
      sessionStorage.removeItem('dashboardAppDistribution');
      fetchDashboardData();
      fetchApplications();
    };
    const handleStorage = (e) => { if (e.key === 'bugtracker:bug-updated') handleBugUpdated(); };
    window.addEventListener('bugtracker:bug-updated', handleBugUpdated);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('bugtracker:bug-updated', handleBugUpdated);
      window.removeEventListener('storage', handleStorage);
    };
  }, [fetchDashboardData, fetchApplications]);

  useEffect(() => {
    if (selectedApp) fetchDashboardData();
  }, [selectedApp, fetchDashboardData]);

  useEffect(() => {
    const handlePopState = () => { setSelectedApp(''); localStorage.removeItem('selectedApp'); };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => fetchDashboardData(), 15000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const getSeverityCount = (severity) => {
    if (Array.isArray(stats?.bySeverity)) {
      const item = stats.bySeverity.find(s => s._id === severity);
      return item ? item.count : 0;
    }
    return 0;
  };

  const getPriorityCount = (priority) => {
    if (Array.isArray(stats?.byPriority)) {
      const item = stats.byPriority.find(p => p._id === priority);
      return item ? item.count : 0;
    }
    return 0;
  };

  const priorityColors = {
    'Low': 'bg-blue-100 text-blue-800',
    'Medium': 'bg-yellow-100 text-yellow-800',
    'High': 'bg-red-100 text-red-800'
  };

  const selectedAppObj = applications.find(app => app._id === selectedApp);

  return (
    <div className="h-screen overflow-y-auto bg-gray-50">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              {selectedApp && (
                <button
                  onClick={() => handleAppChange('')}
                  className="inline-flex items-center justify-center p-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                  title="Navigate Back"
                >
                  <ArrowLeftIcon className="h-5 w-5" />
                </button>
              )}
              {!selectedApp ? (
                <div>
                  <h1 className="text-xl font-semibold text-gray-900 leading-tight whitespace-nowrap">
                    {user.role === 'tester' || user.role === 'developer' ? `Welcome back, ${user?.name || ''}!` : 'Welcome back!'}
                  </h1>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {user.role === 'tester'
                      ? 'Report, track, and manage tickets efficiently.'
                      : user.role === 'developer'
                        ? 'Track, update, and manage assigned tickets efficiently.'
                        : 'Track progress and manage team activity.'}
                  </p>
                </div>
              ) : (
                <div>
                  <h1 className="text-xl font-bold text-gray-900 leading-tight flex items-center gap-2 text-indigo-700">
                    {selectedAppObj?.name || 'Application Analytics'}
                    <span className="text-xs font-medium text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                      v{String(selectedAppObj?.version || '').replace(/^v/i, '') || '1.0.0'}
                    </span>
                  </h1>
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="flex flex-col sm:flex-row sm:flex-wrap lg:flex-nowrap items-stretch sm:items-center gap-2 sm:gap-3 w-full lg:w-auto">
              {user.role === 'developer' && (
                <>
                  <div className="w-full sm:w-64 lg:w-72 flex-shrink-0">
                    <ScrollableDropdown
                      value={selectedApp}
                      onChange={(value) => handleAppChange(value)}
                      options={[
                        { value: '', label: '-- Select Application --' },
                        ...applications.map((app) => ({ value: app._id, label: `${app.name} (v${String(app.version || '').replace(/^v/i, '')})` }))
                      ]}
                      placeholder="-- Select Application --"
                      buttonHeightClassName="h-8"
                      maxMenuHeightPx={280}
                      maxMenuHeightClassName="max-h-72"
                      searchable={true}
                      searchPlaceholder="Search applications..."
                    />
                  </div>
                  <div className="flex w-full sm:w-auto gap-2 sm:gap-3 lg:flex-nowrap">
                    {!selectedApp && (
                      <button
                        onClick={() => navigate('/bugs')}
                        className="inline-flex flex-1 sm:flex-none items-center justify-center px-4 py-2 h-10 border border-transparent text-sm font-semibold rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none transition-all duration-200 shadow-sm whitespace-nowrap"
                        type="button"
                      >
                        <RectangleStackIcon className="h-4 w-4 mr-2" />
                        View Tickets
                      </button>
                    )}
                    <button
                      onClick={() => navigate('/developer/report-bug', { state: { selectedApplication: selectedApp } })}
                      className="inline-flex flex-1 sm:flex-none items-center justify-center px-4 py-2 h-10 border border-transparent text-sm font-semibold rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none transition-colors duration-200 shadow-sm whitespace-nowrap"
                      type="button"
                    >
                      <BugAntIcon className="h-4 w-4 mr-2" />
                      Report Ticket
                    </button>
                    <button
                      onClick={() => navigate('/applications', { state: { openModal: true } })}
                      className="inline-flex flex-1 sm:flex-none items-center justify-center px-3 sm:px-4 py-2 h-10 border border-transparent text-sm font-semibold rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none transition-colors duration-200 whitespace-nowrap shadow-sm"
                      type="button"
                    >
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Add Application
                    </button>
                  </div>
                </>
              )}

              {user.role === 'tester' && (
                <>
                  <div className="w-full sm:w-64 lg:w-72 flex-shrink-0">
                    <ScrollableDropdown
                      value={selectedApp}
                      onChange={(value) => handleAppChange(value)}
                      options={[
                        { value: '', label: '-- Select Application --' },
                        ...applications.map((app) => ({ value: app._id, label: `${app.name} (v${String(app.version || '').replace(/^v/i, '')})` }))
                      ]}
                      placeholder="-- Select Application --"
                      buttonHeightClassName="h-8"
                      maxMenuHeightPx={280}
                      maxMenuHeightClassName="max-h-72"
                      searchable={true}
                      searchPlaceholder="Search applications..."
                    />
                  </div>
                  <div className="flex w-full sm:w-auto gap-2 sm:gap-3 lg:flex-nowrap">
                    {!selectedApp && (
                      <button
                        onClick={() => navigate('/bugs')}
                        className="inline-flex flex-1 sm:flex-none items-center justify-center px-4 py-2 h-10 border border-transparent text-sm font-semibold rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none transition-all duration-200 shadow-sm whitespace-nowrap"
                        type="button"
                      >
                        <RectangleStackIcon className="h-4 w-4 mr-2" />
                        View Tickets
                      </button>
                    )}
                    <button
                      onClick={() => navigate('/bugs/new', { state: { selectedApplication: selectedApp } })}
                      className="inline-flex flex-1 sm:flex-none items-center justify-center px-4 py-2 h-10 border border-transparent text-sm font-semibold rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none transition-colors duration-200 whitespace-nowrap"
                      type="button"
                    >
                      <BugAntIcon className="h-4 w-4 mr-2" />
                      Report Ticket
                    </button>
                    <button
                      onClick={() => {
                        if (!selectedApp) { toast.error('Please select an application first.'); return; }
                        navigate(`/applications/${selectedApp}`, { state: { from: 'tester-dashboard' } });
                      }}
                      className="inline-flex flex-1 sm:flex-none items-center justify-center px-3 sm:px-4 py-2 h-10 border border-transparent text-sm font-semibold rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none transition-colors duration-200 shadow-sm whitespace-nowrap"
                      type="button"
                    >
                      <InformationCircleIcon className="h-4 w-4 mr-2" />
                      View Application Details
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="mb-8">
          <AnalyticsDashboard
            selectedApp={selectedApp}
            onAppChange={handleAppChange}
            applications={applications}
            onCreateAnnouncement={undefined}
            stats={stats}
            getSeverityCount={getSeverityCount}
            getPriorityCount={getPriorityCount}
            severityColors={severityColors}
            priorityColors={priorityColors}
            onRefreshApplications={fetchApplications}
            onRefresh={fetchDashboardData}
            quickActionsSidebar={
              selectedApp && (
                <div className="space-y-4">
                  <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider px-1">Quick Actions</h2>
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={() => { const p = new URLSearchParams(); if (selectedApp) p.set('application', selectedApp); p.set('page', '1'); navigate(`/bugs?${p.toString()}`); }}
                      className="bg-white border border-gray-200 rounded-xl p-3 hover:shadow-md hover:border-blue-300 transition-all text-left w-full group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
                          <ChartBarIcon className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-gray-900 leading-tight">All Tickets</h3>
                          <p className="text-[10px] text-gray-500 mt-0.5">View & filter tickets</p>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => navigate('/kanban', { state: { selectedApplication: selectedApp } })}
                      className="bg-white border border-gray-200 rounded-xl p-3 hover:shadow-md hover:border-blue-300 transition-all text-left w-full group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-100 transition-colors">
                          <ChartBarIcon className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-gray-900 leading-tight">Kanban View</h3>
                          <p className="text-[10px] text-gray-500 mt-0.5">Lifecycle board</p>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => navigate(`/applications/${selectedApp}`)}
                      className="bg-white border border-gray-200 rounded-xl p-3 hover:shadow-md hover:border-blue-300 transition-all text-left w-full group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                          <ExclamationCircleIcon className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-gray-900 leading-tight">App Info</h3>
                          <p className="text-[10px] text-gray-500 mt-0.5">Details & versions</p>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => navigate(`/applications/${selectedApp}/timeline`)}
                      className="bg-white border border-gray-200 rounded-xl p-3 hover:shadow-md hover:border-blue-300 transition-all text-left w-full group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center group-hover:bg-amber-100 transition-colors">
                          <ClockIcon className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-gray-900 leading-tight">Timeline</h3>
                          <p className="text-[10px] text-gray-500 mt-0.5">Activity history</p>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              )
            }
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
